"use client";

import { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { hasAnalyticsConsent } from "@/lib/cookieConsent";
import { sendPlausibleEvent } from "@/lib/track";

declare global {
  interface Window {
    gtag?: (command: string, ...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

export function GoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [hasConsent, setHasConsent] = useState(false);

  // Check consent on mount and when storage changes
  useEffect(() => {
    const checkConsent = () => {
      setHasConsent(hasAnalyticsConsent());
    };

    checkConsent();

    // Listen for storage changes (consent updates)
    window.addEventListener("storage", checkConsent);

    // Also listen for custom consent events
    const handleConsentChange = () => checkConsent();
    window.addEventListener("cookie-consent-updated", handleConsentChange);

    return () => {
      window.removeEventListener("storage", checkConsent);
      window.removeEventListener("cookie-consent-updated", handleConsentChange);
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "development" && hasConsent && !GA_MEASUREMENT_ID) {
      // Keep this visible only in dev to avoid silent misconfiguration.
      // eslint-disable-next-line no-console
      console.warn(
        "[analytics] Missing NEXT_PUBLIC_GA_MEASUREMENT_ID. Pageviews are not being tracked.",
      );
    }
  }, [hasConsent]);

  // Track page views on route changes
  useEffect(() => {
    if (!hasConsent || !GA_MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;

    const search = searchParams?.toString();
    const path = pathname + (search ? `?${search}` : "");

    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }, [pathname, searchParams, hasConsent]);

  if (!hasConsent || !GA_MEASUREMENT_ID) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', {
            anonymize_ip: true,
            cookie_flags: 'SameSite=None;Secure'
          });
        `}
      </Script>
    </>
  );
}

// Helper function to track events
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>
) {
  if (typeof window === "undefined") return;
  // Plausible is cookieless: no consent gate (the check below is for GA only).
  sendPlausibleEvent(eventName, eventParams);
  if (!hasAnalyticsConsent() || !window.gtag) return;

  window.gtag("event", eventName, eventParams);
}
